import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2, Shield, MapPin, Mail, Search, UserX, Key, Eye, EyeOff } from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';

interface Municipality {
  id: string;
  name: string;
}

interface UserWithAssignments {
  id: string;
  email: string;
  role: 'superadmin' | 'admin' | null;
  municipalities: Municipality[];
}

interface AdminManagementProps {
  onClose: () => void;
}

export const AdminManagement = ({ onClose }: AdminManagementProps) => {
  const { toast } = useToast();
  const [users, setUsers] = useState<UserWithAssignments[]>([]);
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedMunicipalityId, setSelectedMunicipalityId] = useState<string>('');
  
  // Delete user states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserWithAssignments | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Password change states
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [userToChangePassword, setUserToChangePassword] = useState<UserWithAssignments | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [municipalitiesData, usersData] = await Promise.all([
        api.municipalities.list(),
        api.adminUsers.list(),
      ]);

      setMunicipalities(municipalitiesData.map((m: any) => ({ id: m.id, name: m.name })));

      setUsers(
        usersData.map((u: any) => ({
          id: u.id,
          email: u.email,
          role: u.role,
          municipalities: (u.assignedMunicipalities ?? []).map((mid: string) => {
            const muni = municipalitiesData.find((m: any) => m.id === mid);
            return muni ? { id: muni.id, name: muni.name } : { id: mid, name: mid };
          }),
        })),
      );
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignMunicipality = async () => {
    if (!selectedUserId || !selectedMunicipalityId) return;

    try {
      await api.adminUsers.assignMunicipality(selectedUserId, selectedMunicipalityId);

      toast({
        title: 'Municipio asignado',
        description: 'El administrador ha sido asignado al municipio',
      });

      setAssignDialogOpen(false);
      setSelectedUserId('');
      setSelectedMunicipalityId('');
      loadData();
    } catch (error: any) {
      console.error('Error assigning municipality:', error);
      toast({
        title: 'Error',
        description: error.message?.includes('duplicate')
          ? 'El usuario ya está asignado a este municipio'
          : 'No se pudo asignar el municipio',
        variant: 'destructive',
      });
    }
  };

  const handleRemoveAssignment = async (userId: string, municipalityId: string, municipalityName: string) => {
    try {
      await api.adminUsers.removeMunicipality(userId, municipalityId);

      toast({
        title: 'Asignación eliminada',
        description: `Se ha eliminado la asignación a ${municipalityName}`,
      });

      loadData();
    } catch (error) {
      console.error('Error removing assignment:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la asignación',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    setDeleting(true);
    try {
      await api.adminUsers.delete(userToDelete.id);

      toast({
        title: 'Usuario eliminado',
        description: `El usuario ${userToDelete.email} ha sido eliminado del sistema`,
      });

      setDeleteDialogOpen(false);
      setUserToDelete(null);
      loadData();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo eliminar el usuario',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (user: UserWithAssignments) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const openPasswordDialog = (user: UserWithAssignments) => {
    setUserToChangePassword(user);
    setNewPassword('');
    setShowNewPassword(false);
    setPasswordDialogOpen(true);
  };

  const handleChangePassword = async () => {
    if (!userToChangePassword || !newPassword) return;

    if (newPassword.length < 8) {
      toast({
        title: 'Error',
        description: 'La contraseña debe tener al menos 8 caracteres',
        variant: 'destructive',
      });
      return;
    }

    setChangingPassword(true);
    try {
      await api.adminUsers.changePassword(userToChangePassword.id, newPassword);

      toast({
        title: 'Contraseña actualizada',
        description: `La contraseña de ${userToChangePassword.email} ha sido cambiada`,
      });

      setPasswordDialogOpen(false);
      setUserToChangePassword(null);
      setNewPassword('');
    } catch (error: any) {
      console.error('Error changing password:', error);
      toast({
        title: "Error",
        description: error.message || "No se pudo cambiar la contraseña",
        variant: "destructive"
      });
    } finally {
      setChangingPassword(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.municipalities.some(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <Card className="border-2 shadow-xl">
        <CardHeader className="border-b bg-muted/30">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96 mt-2" />
        </CardHeader>
        <CardContent className="p-8 space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-2 shadow-xl">
        <CardHeader className="border-b bg-muted/30 p-4 md:p-6">
          <div>
            <CardTitle className="text-xl md:text-2xl flex items-center gap-2">
              <Shield className="h-5 w-5 md:h-6 md:w-6" />
              Gestión de Administradores
            </CardTitle>
            <CardDescription className="text-sm md:text-base mt-2">
              Asigna administradores a municipios específicos
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="p-4 md:p-8">
          {/* Search */}
          <div className="relative mb-4 md:mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Buscar por email, ID o municipio..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {filteredUsers.length === 0 ? (
            <div className="text-center py-16">
              <Shield className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No hay administradores</h3>
              <p className="text-muted-foreground">
                Los usuarios que se registren aparecerán aquí
              </p>
            </div>
          ) : (
            <div className="space-y-3 md:space-y-4">
              {filteredUsers.map((user) => (
                <Card key={user.id} className="overflow-hidden">
                  <div className="p-3 md:p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div className="space-y-2 min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Badge variant={user.role === 'superadmin' ? 'default' : user.role === 'admin' ? 'secondary' : 'outline'}>
                            {user.role === 'superadmin' ? 'Superadmin' : user.role === 'admin' ? 'Admin Municipal' : 'Sin rol'}
                          </Badge>
                        </div>
                        {user.email && (
                          <p className="text-sm flex items-center gap-1 break-all">
                            <Mail className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            <span className="truncate">{user.email}</span>
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground font-mono">
                          ID: {user.id.substring(0, 8)}...
                        </p>
                        
                        {user.role !== 'superadmin' && (
                          <div className="mt-2 md:mt-3">
                            <p className="text-sm font-medium mb-2">Municipios asignados:</p>
                            {user.municipalities.length === 0 ? (
                              <p className="text-sm text-muted-foreground italic">Sin municipios asignados</p>
                            ) : (
                              <div className="flex flex-wrap gap-1.5 md:gap-2">
                                {user.municipalities.map((m) => (
                                  <Badge key={m.id} variant="outline" className="flex items-center gap-1 text-xs md:text-sm">
                                    <MapPin className="h-3 w-3" />
                                    {m.name}
                                    <button
                                      onClick={() => handleRemoveAssignment(user.id, m.id, m.name)}
                                      className="ml-1 hover:text-destructive"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {user.role !== 'superadmin' && (
                        <div className="flex flex-wrap items-center gap-2 pt-2 md:pt-0 border-t md:border-t-0 border-border/50">
                          <Dialog open={assignDialogOpen && selectedUserId === user.id} onOpenChange={(open) => {
                            setAssignDialogOpen(open);
                            if (open) setSelectedUserId(user.id);
                          }}>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" className="flex-1 md:flex-none">
                                <MapPin className="mr-1.5 md:mr-2 h-4 w-4" />
                                <span className="hidden sm:inline">Asignar Municipio</span>
                                <span className="sm:hidden">Asignar</span>
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="mx-4 max-w-md">
                              <DialogHeader>
                                <DialogTitle>Asignar municipio</DialogTitle>
                                <DialogDescription>
                                  Selecciona un municipio para asignar a este administrador
                                </DialogDescription>
                              </DialogHeader>
                              <div className="py-4">
                                <Select value={selectedMunicipalityId} onValueChange={setSelectedMunicipalityId}>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar municipio" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {municipalities
                                      .filter(m => !user.municipalities.some(um => um.id === m.id))
                                      .map((m) => (
                                        <SelectItem key={m.id} value={m.id}>
                                          {m.name}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <DialogFooter className="flex-col sm:flex-row gap-2">
                                <Button variant="outline" onClick={() => setAssignDialogOpen(false)} className="w-full sm:w-auto">
                                  Cancelar
                                </Button>
                                <Button onClick={handleAssignMunicipality} disabled={!selectedMunicipalityId} className="w-full sm:w-auto">
                                  Asignar
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                          
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openPasswordDialog(user)}
                            title="Cambiar contraseña"
                          >
                            <Key className="h-4 w-4" />
                          </Button>
                          
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => openDeleteDialog(user)}
                          >
                            <UserX className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambiar contraseña</DialogTitle>
            <DialogDescription>
              Establece una nueva contraseña para {userToChangePassword?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nueva contraseña</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={changingPassword}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className={`text-xs ${newPassword.length >= 6 ? 'text-green-600' : 'text-muted-foreground'}`}>
                Mínimo 6 caracteres {newPassword.length > 0 && `(${newPassword.length}/6)`}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setPasswordDialogOpen(false)}
              disabled={changingPassword}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleChangePassword} 
              disabled={changingPassword || newPassword.length < 6}
            >
              {changingPassword ? "Cambiando..." : "Cambiar contraseña"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Eliminar usuario"
        description={`¿Estás seguro de que deseas eliminar al usuario ${userToDelete?.email}? Esta acción no se puede deshacer y eliminará todos sus datos y asignaciones.`}
        confirmText={deleting ? "Eliminando..." : "Eliminar usuario"}
        onConfirm={handleDeleteUser}
        variant="destructive"
      />
    </>
  );
};
